import { BulkResolvedContact } from "../hooks/useBulkResolveContacts";

export interface BackendContact {
    firstName?: string;
    lastName?: string;
    name?: string;
    businessName?: string | null;
    role?: string;
    type?: string;
    phoneNumber?: string;
}

export type AnyResolvedContact = BulkResolvedContact | BackendContact;

const formatContactType = (type?: string) => {
    if (!type) return "";

    return type
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const roleLabels: Record<string, string> = {
    CLIENT: "Client",
    WORKFORCE: "Workforce",
    ADMIN: "Admin",
    OTHER: "Other",
    client: "Client",
    workforce: "Workforce",
    admin: "Admin",
    other: "Other",
};

export function getDisplayName(
    resolved: AnyResolvedContact | null | undefined,
): string | null {
    if (!resolved) return null;

    const firstName =
        (resolved as any).firstName || (resolved as any).name || "";

    const lastName = (resolved as any).lastName || "";
    const businessName = (resolved as any).businessName || "";

    const nameFromFirstLast = [firstName, lastName]
        .filter(Boolean)
        .join(" ");

    if (nameFromFirstLast) return nameFromFirstLast;
    if (businessName) return businessName;

    return null;
}

export function getRoleLabel(
    resolved: AnyResolvedContact | null | undefined,
): string {
    if (!resolved) return "";

    const roleOrType = (resolved as any).role || (resolved as any).type || "";

    return roleLabels[roleOrType] || formatContactType(roleOrType);
}

export function getCallerType(
    resolved: AnyResolvedContact | null | undefined,
): "client" | "workforce" | null {
    if (!resolved) return null;

    const type = (resolved as any).type || (resolved as any).role || "";

    if (type.toLowerCase().includes("workforce")) return "workforce";

    if (type.toLowerCase().includes("client")) return "client";

    return null;
}

const normalizePhoneDigits = (phone?: string | null) => String(phone ?? "").replace(/\D/g, "");

/** Look up a resolved contact when map keys may differ slightly in formatting. */
export function findResolvedByPhone<T>(
    map: Record<string, T> | null | undefined,
    phone?: string | null,
): T | null {
    if (!map || !phone) return null;

    if (map[phone]) return map[phone];

    const target = normalizePhoneDigits(phone);
    if (!target) return null;

    for (const [key, value] of Object.entries(map)) {
        const digits = normalizePhoneDigits(key);
        if (!digits) continue;
        if (digits === target || digits.endsWith(target) || target.endsWith(digits)) {
            return value;
        }
    }

    return null;
}